import './Leaf.scss'

import React, { FC, useEffect, useRef } from 'react'

interface LeafProps {
  className?: string
  direction?: 'left' | 'right'
  style?: React.CSSProperties
  pauseDuration?: number
}

export const Leaf: FC<LeafProps> = ({
  className,
  direction = 'right',
  style,
  pauseDuration = 3,
}) => {
  const leafRef = useRef<SVGSVGElement>(null)
  const uniqueId = useRef(Math.random().toString(36).substring(2, 9)) // Generate a unique ID for each leaf

  const getRandomPauseDuration = () => {
    return Math.random() * (6 - 1) + 1
  }

  useEffect(() => {
    const leafElement = leafRef.current
    if (leafElement) {
      const updateAnimation = () => {
        const pause = getRandomPauseDuration()
        const totalDuration = 0.3 + pause // 0.3 seconds for rotation + pause duration

        // Reset animation using requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          leafElement.style.animation = 'none' // Reset animation
          leafElement.getBoundingClientRect() // Force reflow
          leafElement.style.animation = `wind-blow-${uniqueId.current} ${totalDuration}s cubic-bezier(0.42, 0, 0.58, 1)`
        })
      }

      // Set initial animation
      updateAnimation()

      // Update animation on each iteration
      leafElement.addEventListener('animationend', updateAnimation)

      // Cleanup event listener on component unmount
      return () => {
        leafElement.removeEventListener('animationend', updateAnimation)
      }
    }
  }, [pauseDuration]) // Depend on pauseDuration to update animation when it changes

  if (direction === 'left') {
    return (
      <svg
        className={className}
        fill={'none'}
        height={'23'}
        ref={leafRef}
        style={{ ...style }}
        viewBox={'0 0 28 23'}
        width={'28'}
        xmlns={'http://www.w3.org/2000/svg'}
      >
        <style>
          {`
          @keyframes wind-blow-${uniqueId.current} {
            0% {
              transform: rotate(0deg);
            }
            9.09% {
              transform: rotate(25deg);
            }
            18.18% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(0deg);
            }
          }
        `}
        </style>
        <path
          d={
            'M19.0249 21.1729L8.74521 20.0582C8.32623 20.0188 8.00468 19.7426 7.83904 19.3875C4.93539 13.9326 2.62612 8.19174 0.170686 2.52976C-0.121627 1.91819 0.141455 1.17838 0.755313 0.912055C0.969676 0.813414 1.19378 0.793686 1.40815 0.823278C2.91843 1.06002 4.42872 1.28689 5.94874 1.48417C8.56982 1.85901 12.3796 2.31275 15.0494 2.61854L19.6095 3.09202C20.0772 3.14134 20.4475 3.43726 20.6229 3.84168C23.1855 9.37543 26.2937 14.9684 27.9404 20.8572C28.0769 21.3701 27.7748 21.9028 27.2681 22.0409C26.8686 22.1494 26.4594 21.9817 26.2353 21.6562C25.3583 20.364 24.5983 19.0126 23.9065 17.6317C21.948 13.6268 20.0285 9.05978 18.2746 4.93659L19.288 5.68627L14.7571 4.97605C10.3627 4.33489 5.51027 3.67399 1.11583 3.22025L2.35329 1.51376C4.99385 7.09683 7.78057 12.6108 9.96318 18.4011L9.05701 17.7303L19.2782 19.279C20.467 19.4762 20.2429 21.2814 19.0346 21.163L19.0249 21.1729Z'
          }
          fill={'#181716'}
        />
      </svg>
    )
  }
  return (
    <svg
      className={className}
      fill={'none'}
      height={'21'}
      ref={leafRef}
      style={{ ...style }}
      viewBox={'0 0 23 21'}
      width={'23'}
      xmlns={'http://www.w3.org/2000/svg'}
    >
      <style>
        {`
          @keyframes wind-blow-${uniqueId.current} {
            0% {
              transform: rotate(0deg);
            }
            9.09% {
              transform: rotate(25deg);
            }
            18.18% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(0deg);
            }
          }
        `}
      </style>
      <path
        d={
          'M1.99222 18.6526C5.95793 17.6267 10.0016 17.1631 14.0355 16.6699L12.9832 17.459C14.8637 13.4246 17.0951 8.95619 18.9269 4.90206C19.5895 3.51122 20.1936 2.0908 20.8172 0.670371L21.9962 2.28808C15.9453 3.13639 9.92365 4.24116 3.85328 4.98097L4.9933 4.13266C4.03841 6.18439 3.02506 8.19665 1.72914 10.0708C1.42708 10.5048 0.822966 10.6134 0.39424 10.2977C0.0629513 10.061 -0.0734626 9.64667 0.0239752 9.27184C0.296801 8.1572 0.657322 7.08202 1.05682 6.02656C1.44657 4.96124 1.88504 3.92551 2.34299 2.89965C2.55736 2.42618 2.98608 2.12039 3.46353 2.05134C9.52415 1.21289 15.6432 0.73942 21.7234 0.00947855C22.5808 -0.118754 23.2726 0.857788 22.8829 1.63705C22.23 3.03775 21.5675 4.42858 20.9536 5.84901C19.8623 8.2953 18.3228 11.8464 17.2705 14.3518C16.6566 15.7723 16.072 17.2025 15.4386 18.6032C15.234 19.0471 14.8345 19.3332 14.3863 19.3924C10.3621 19.9448 6.33794 20.5267 2.23581 20.5563C1.13477 20.5662 0.930147 18.9288 1.99222 18.6723V18.6526Z'
        }
        fill={'#181716'}
      />
    </svg>
  )
}
